/**
 * The side of the person who answers a client's request about their personal data (S-32): the closed
 * list of grounds the law gives for not granting a right, and when each form is complete enough to send.
 * The server decides everything else (the periods, that a client with regulated records is never
 * granted a full cancellation, that a rectification goes through a KYC case of the client).
 */
import { isMfaCode } from './mfaApproval';
import type { DataRight, Determination, StaffDataRequest } from '../services/dataRequests.service';

export interface Ground {
  code: string;
  label: string;
}

/** LFPDPPP art. 33 (why a right is not granted), art. 25 (why data is not cancelled), art. 26 (opposition). */
export const GROUNDS: readonly Ground[] = [
  { code: 'art33_not_accredited', label: 'Art. 33 I: the person or representative is not accredited' },
  { code: 'art33_not_held', label: 'Art. 33 II: the data is not held by us' },
  { code: 'art33_third_party', label: 'Art. 33 III: it would harm the rights of a third party' },
  { code: 'art33_legal_impediment', label: 'Art. 33 IV: a legal impediment or an authority’s resolution' },
  { code: 'art33_already_done', label: 'Art. 33 V: it was already done' },
  { code: 'art25_contract', label: 'Art. 25 I: data of a contract, needed for its performance' },
  { code: 'art25_legal_duty', label: 'Art. 25 II: data the law requires us to treat' },
  { code: 'art25_fiscal_judicial', label: 'Art. 25 III: it would obstruct fiscal, judicial or administrative acts' },
  { code: 'art25_holder_interest', label: 'Art. 25 IV: needed to protect the holder’s own interests' },
  { code: 'art25_public_interest', label: 'Art. 25 V: needed for an action in the public interest' },
  { code: 'art25_holder_obligation', label: 'Art. 25 VI: needed to meet an obligation the holder took on' },
  { code: 'art26_legal_obligation', label: 'Art. 26: the use is needed to meet a legal obligation' },
];

export const DETERMINATION_LABEL: Record<Determination, string> = {
  accepted: 'Accept',
  partial: 'Accept in part',
  refused: 'Refuse',
};

export const RECORD_LABEL: Record<string, string> = {
  ledger: 'Ledger entries',
  contracts: 'Signed contracts',
  kyc_file: 'KYC file',
};

const NOTE_MIN = 10;
const RESPONSE_MIN = 10;

/** The answer can be sent: a response, a ground unless it is accepted, and the authentication code. */
export function canAnswer(input: {
  determination: Determination;
  ground: string;
  response: string;
  code: string;
}): boolean {
  const groundOk = input.determination === 'accepted' || input.ground !== '';
  return groundOk && input.response.trim().length >= RESPONSE_MIN && isMfaCode(input.code.trim());
}

/** What was granted is carried out with a note; a rectification also names the client's KYC case. */
export function canExecute(input: {
  right: DataRight;
  note: string;
  kycCaseId: string;
  code: string;
}): boolean {
  const caseOk = input.right !== 'rectification' || input.kycCaseId.trim() !== '';
  return caseOk && input.note.trim().length >= NOTE_MIN && isMfaCode(input.code.trim());
}

/** Where the request is, for the person who works it. */
export function nextStep(request: Pick<StaffDataRequest, 'status' | 'extended'>): string {
  switch (request.status) {
    case 'received':
      return request.extended ? 'Waiting for the answer (extended once)' : 'Waiting for the answer';
    case 'answered':
      return 'Answered: waiting to be carried out';
    case 'executed':
      return 'Carried out';
    default:
      return 'Answered, nothing to carry out';
  }
}

export function isLate(request: Pick<StaffDataRequest, 'answer_overdue' | 'execution_overdue'>): boolean {
  return request.answer_overdue || request.execution_overdue;
}

/** The date that matters now, for the person who works the request, shown without opening it. */
export function dueLine(
  request: Pick<
    StaffDataRequest,
    'status' | 'answer_due_on' | 'execute_due_on' | 'answered_at' | 'executed_at'
  >,
  formatDay: (day: string) => string,
  formatMoment: (moment: string) => string,
): string | null {
  if (request.status === 'received') return `Answer due ${formatDay(request.answer_due_on)}`;
  if (request.status === 'answered' && request.execute_due_on) {
    return `To be carried out by ${formatDay(request.execute_due_on)}`;
  }
  if (request.status === 'executed' && request.executed_at) {
    return `Carried out ${formatMoment(request.executed_at)}`;
  }
  return request.answered_at ? `Answered ${formatMoment(request.answered_at)}` : null;
}

/** A client with records the law requires to be kept is never granted a full cancellation (the
 *  server refuses it): the form does not offer it. */
export function mayAcceptInFull(
  request: Pick<StaffDataRequest, 'right' | 'retention_holds'>,
): boolean {
  return !(request.right === 'cancellation' && request.retention_holds.length > 0);
}
